import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Search, Send, Edit2, X, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { API, authFetch } from '../utils/api';

const emptyForm = { name: '', email: '', phone: '', company: '', vehicle_ids: [], notify_wash: true };

export default function Customers({ user }) {
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [searchQuery, setSearchQuery] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [sendingReport, setSendingReport] = useState(null);

  const location = useLocation();

  useEffect(() => {
    fetchCustomers();
    fetchVehicles();
    const interval = setInterval(fetchCustomers, 30000);
    const onFocus = () => { fetchCustomers(); fetchVehicles(); };
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [location.key]);

  const fetchCustomers = async () => {
    try {
      const res = await authFetch(`${API}/customers`);
      if (!res.ok) throw new Error();
      setCustomers(await res.json());
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  };

  const fetchVehicles = async () => {
    try {
      const res = await authFetch(`${API}/vehicles`);
      if (res.ok) setVehicles(await res.json());
    } catch {}
  };

  const openNew = () => {
    setEditing(null);
    setFormData({ ...emptyForm });
    setVehicleSearch('');
    setDialogOpen(true);
  };

  const openEdit = (cust) => {
    setEditing(cust);
    setFormData({
      name: cust.name || '',
      email: cust.email || '',
      phone: cust.phone || '',
      company: cust.company || '',
      vehicle_ids: cust.vehicle_ids || [],
      notify_wash: cust.notify_wash !== false,
    });
    setVehicleSearch('');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editing ? `${API}/customers/${editing.customer_id}` : `${API}/customers`;
    const method = editing ? 'PUT' : 'POST';
    try {
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success(editing ? 'Customer updated' : 'Customer added');
      setDialogOpen(false);
      fetchCustomers();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (cust) => {
    if (!window.confirm(`Delete customer "${cust.name}"?`)) return;
    try {
      const res = await authFetch(`${API}/customers/${cust.customer_id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      toast.success('Customer deleted');
      fetchCustomers();
    } catch { toast.error('Failed to delete customer'); }
  };

  const handleSendReport = async (cust) => {
    setSendingReport(cust.customer_id);
    try {
      const res = await authFetch(`${API}/customers/${cust.customer_id}/send-wash-report`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message || 'Report sent');
    } catch (err) { toast.error(err.message); }
    finally { setSendingReport(null); }
  };

  const toggleVehicle = (vid) => {
    setFormData(prev => ({
      ...prev,
      vehicle_ids: prev.vehicle_ids.includes(vid)
        ? prev.vehicle_ids.filter(id => id !== vid)
        : [...prev.vehicle_ids, vid]
    }));
  };

  const getVehicleLabel = (vid) => {
    const v = vehicles.find(veh => veh.vehicle_id === vid);
    return v ? `${v.unit_number} - ${v.license_plate}` : vid;
  };

  const filteredCustomers = customers.filter(c => {
    const q = searchQuery.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.company || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q);
  });

  const filteredVehicles = vehicles.filter(v => {
    const q = vehicleSearch.toLowerCase();
    if (!q) return true;
    return (v.unit_number || '').toLowerCase().includes(q) ||
      (v.license_plate || '').toLowerCase().includes(q) ||
      (v.make || '').toLowerCase().includes(q);
  });

  return (
    <Layout user={user}>
      <div className="p-4 lg:p-12 space-y-6 lg:space-y-8 bg-slate-200 min-h-screen">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl lg:text-5xl font-heading font-bold tracking-tight" data-testid="customers-title">Customers</h1>
            <p className="text-slate-600 text-sm lg:text-base mt-1">Manage customer profiles and vehicle groups</p>
          </div>
          <Button onClick={openNew} data-testid="add-customer-button">
            <UserPlus className="h-4 w-4 mr-2" />
            ADD CUSTOMER
          </Button>
        </div>

        {/* Search */}
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-50 border-slate-200"
              data-testid="customer-search"
            />
          </div>
        </div>

        {/* Customer Cards */}
        {filteredCustomers.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-slate-500" data-testid="no-customers">
            {searchQuery ? `No customers found matching "${searchQuery}"` : 'No customers yet. Add your first customer.'}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCustomers.map((cust) => (
              <div key={cust.customer_id} className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid={`customer-card-${cust.customer_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-base">{cust.name}</h3>
                    {cust.company && <p className="text-sm text-slate-500">{cust.company}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(cust)} data-testid={`edit-customer-${cust.customer_id}`}>
                      <Edit2 className="h-4 w-4 text-blue-500" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(cust)} data-testid={`delete-customer-${cust.customer_id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                {cust.email && <p className="text-sm text-slate-600">{cust.email}</p>}
                {cust.phone && <p className="text-sm text-slate-600">{cust.phone}</p>}

                {/* Vehicle group */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Vehicles ({(cust.vehicle_ids || []).length})</p>
                  {(cust.vehicle_ids || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {cust.vehicle_ids.map(vid => (
                        <span key={vid} className="inline-block bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded font-medium">
                          {getVehicleLabel(vid)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">No vehicles assigned</p>
                  )}
                </div>

                {/* Send report button */}
                {cust.email && (cust.vehicle_ids || []).length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs uppercase font-bold"
                    onClick={() => handleSendReport(cust)}
                    disabled={sendingReport === cust.customer_id}
                    data-testid={`send-report-${cust.customer_id}`}
                  >
                    <Send className="h-3.5 w-3.5 mr-2" />
                    {sendingReport === cust.customer_id ? 'Sending...' : 'Send Wash Report'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditing(null); setFormData({ ...emptyForm }); } }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">
                {editing ? 'Edit Customer' : 'Add Customer'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="uppercase text-xs font-bold tracking-wide">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Customer name"
                    required
                    className="bg-slate-50 border-slate-200"
                    data-testid="customer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="uppercase text-xs font-bold tracking-wide">Company</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Company name"
                    className="bg-slate-50 border-slate-200"
                    data-testid="customer-company-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="uppercase text-xs font-bold tracking-wide">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                    className="bg-slate-50 border-slate-200"
                    data-testid="customer-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="uppercase text-xs font-bold tracking-wide">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="bg-slate-50 border-slate-200"
                    data-testid="customer-phone-input"
                  />
                </div>
              </div>

              {/* Vehicle Assignment */}
              <div className="space-y-2">
                <Label className="uppercase text-xs font-bold tracking-wide">Assign Vehicles</Label>
                {formData.vehicle_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {formData.vehicle_ids.map(vid => (
                      <span key={vid} className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs px-2 py-1 rounded font-medium">
                        {getVehicleLabel(vid)}
                        <button type="button" onClick={() => toggleVehicle(vid)} className="hover:text-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <Input
                  placeholder="Search vehicles to add..."
                  value={vehicleSearch}
                  onChange={(e) => setVehicleSearch(e.target.value)}
                  className="bg-slate-50 border-slate-200"
                  data-testid="vehicle-search-input"
                />
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md">
                  {filteredVehicles.length === 0 ? (
                    <p className="p-3 text-sm text-slate-400 text-center">No vehicles found</p>
                  ) : (
                    filteredVehicles.map(v => {
                      const selected = formData.vehicle_ids.includes(v.vehicle_id);
                      return (
                        <button
                          key={v.vehicle_id}
                          type="button"
                          onClick={() => toggleVehicle(v.vehicle_id)}
                          className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 transition-colors ${selected ? 'bg-green-50 text-green-800 font-medium' : 'hover:bg-slate-50'}`}
                          data-testid={`vehicle-option-${v.vehicle_id}`}
                        >
                          <span className="font-semibold">{v.unit_number}</span> - {v.license_plate} ({v.make} {v.model})
                          {selected && <span className="float-right text-green-600 font-bold">&#10003;</span>}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <Button type="submit" className="w-full uppercase font-bold" data-testid="customer-submit-button">
                {editing ? 'Update Customer' : 'Add Customer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
